import React, { useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassDataTable, GlassStatusBadge } from '@/components/admin/redesign';
import { FilterChipGroup } from '@/components/shared/redesign';
import { TransactionCard } from '@/components/admin/resident/mobile/TransactionCard';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useFontScale } from '@/hooks/shared/useFontScale';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { formatDateShort } from '@/utils/admin/date';
import { IAdminTransaction } from '@/types/admin/transactions';

type Tab = 'all' | 'payments' | 'charges' | 'pending' | 'voided';

interface Props {
  transactions: IAdminTransaction[];
}

export const TransactionsBody: React.FC<Props> = ({ transactions }) => {
  const styles = useStyles();
  const { showFullTable } = useResponsive();
  // Five fixed-width columns cannot fit large text even on a tablet; the card list already exists for phones.
  const { isLargeText } = useFontScale();
  const [tab, setTab] = useState<Tab>('all');

  const filtered = useMemo(() => {
    switch (tab) {
      case 'payments':
        return transactions.filter((t) => t.type === 'payment');
      case 'charges':
        return transactions.filter((t) => t.type === 'charge');
      case 'pending':
        return transactions.filter(
          (t) => (t.paymentSettlementStatus ?? '').toLowerCase() === 'pending',
        );
      case 'voided':
        return transactions.filter(
          (t) => (t.paymentSettlementStatus ?? '').toLowerCase() === 'voided',
        );
      default:
        return transactions;
    }
  }, [transactions, tab]);

  const filterOptions = [
    { value: 'all' as const, label: 'All' },
    { value: 'payments' as const, label: 'Payments' },
    { value: 'charges' as const, label: 'Charges' },
    { value: 'pending' as const, label: 'Pending' },
    { value: 'voided' as const, label: 'Voided' },
  ];

  const emptyState = (
    <View style={styles.empty}>
      <Text style={styles.emptyText}>No transactions match these filters</Text>
    </View>
  );

  return (
    <View style={{ gap: 12 }}>
      <FilterChipGroup options={filterOptions} value={tab} onChange={setTab} centered />
      {showFullTable && !isLargeText ? (
        <GlassDataTable
          density="comfortable"
          header={
            <View style={[styles.row, styles.thead]}>
              <Text style={[styles.cell, styles.colDate]}>Date</Text>
              <Text style={[styles.cell, styles.colDesc]}>Description</Text>
              <Text style={[styles.cell, styles.colType]}>Type</Text>
              <Text style={[styles.cell, styles.colAmount]}>Amount</Text>
              <Text style={[styles.cell, styles.colStatus]}>Status</Text>
            </View>
          }
        >
          {filtered.map((t) => (
            <View key={String(t.transactionId)} style={styles.row}>
              <Text style={[styles.cell, styles.colDate]}>
                {formatDateShort(new Date(t.transactionDate))}
              </Text>
              <Text style={[styles.cell, styles.colDesc]} numberOfLines={1}>
                {t.description}
              </Text>
              <View style={styles.colType}>
                <GlassStatusBadge
                  variant={t.type === 'payment' ? 'info' : 'neutral'}
                  label={t.type ?? '—'}
                />
              </View>
              <Text
                style={[
                  styles.cell,
                  styles.colAmount,
                  t.amount > 0 ? styles.success : styles.error,
                ]}
              >
                {t.amount > 0 ? '+' : ''}${Math.abs(t.amount).toFixed(2)}
              </Text>
              <View style={styles.colStatus}>
                <GlassStatusBadge
                  variant={
                    (t.paymentSettlementStatus ?? '').toLowerCase() === 'pending'
                      ? 'warning'
                      : 'success'
                  }
                  label={t.paymentSettlementStatus ?? 'Cleared'}
                />
              </View>
            </View>
          ))}
          {filtered.length === 0 ? emptyState : null}
        </GlassDataTable>
      ) : (
        <View style={{ gap: 8 }}>
          {filtered.map((t) => (
            <TransactionCard key={String(t.transactionId)} transaction={t} />
          ))}
          {filtered.length === 0 ? emptyState : null}
        </View>
      )}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ colors, components, semantic }) =>
    StyleSheet.create({
      row: {
        flexDirection: 'row',
        gap: 8,
        paddingVertical: 10,
        paddingHorizontal: 12,
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
      },
      thead: { backgroundColor: components.admin.dataTable.headerBg },
      cell: { color: colors.text.primary, fontSize: 12 },
      colDate: { width: 96 },
      colDesc: { flex: 2 },
      colType: { width: 90 },
      colAmount: { width: 96, textAlign: 'right' },
      colStatus: { width: 110 },
      success: { color: semantic?.status?.success ?? colors.text.primary },
      error: { color: semantic?.status?.error ?? colors.text.primary },
      empty: { padding: 16, alignItems: 'center' },
      emptyText: { color: colors.text.dim, fontSize: 13 },
    }),
  );
